/**
 * F-148 §10.1 `/stock/receiving`: Drafts / Receipts tabs. Gating via `receiving_get_access`:
 * `!canManage` → not available; `canManage && !enabled` → Receipts tab with drafts read-only and
 * the paused banner; otherwise the full flow with the "Receive a delivery" entry.
 */
import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PackagePlus } from 'lucide-react';
import { useAuthSession, useTranslation } from '@dukanti/shared';
import { useReceivingAccess, useReceivingList } from '@dukanti/shared/hooks';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { Button } from '@/components/ui/button';
import {
  ReceivingList,
  ReceivingNotAvailable,
  ReceivingPausedBanner,
  type ReceivingListKind,
} from '@/features/stock/receiving/components';

export function ReceivingListRoute(): React.JSX.Element {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const access = useReceivingAccess(supabase, storeId);

  const canManage = access.data?.canManage ?? false;
  const enabled = access.data?.enabled ?? false;
  const requestedKind: ReceivingListKind =
    searchParams.get('tab') === 'receipts' ? 'receipt' : 'draft';
  const kind: ReceivingListKind =
    access.data && !enabled
      ? requestedKind === 'draft' && searchParams.get('tab') !== 'drafts'
        ? 'receipt'
        : requestedKind
      : requestedKind;

  const list = useReceivingList(supabase, canManage ? storeId : null, kind);
  const items = list.data?.pages.flatMap((page) => page.items) ?? [];

  const onKindChange = useCallback(
    (next: ReceivingListKind) => {
      setSearchParams(next === 'receipt' ? { tab: 'receipts' } : { tab: 'drafts' }, {
        replace: true,
      });
    },
    [setSearchParams],
  );

  return (
    <main
      dir={dir}
      data-testid="receiving-list-route"
      className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 lg:px-8"
    >
      <header className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div className="min-w-0 text-start">
          <h1 className="font-display text-foreground text-[28px] font-bold leading-tight tracking-tight">
            {t('admin.receiving.title')}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">{t('admin.receiving.subtitle')}</p>
        </div>
        {canManage && enabled ? (
          <Button
            type="button"
            className="min-h-11 w-fit shrink-0"
            onClick={() => navigate('/stock/receiving/new')}
            data-testid="receiving-new-button"
          >
            <PackagePlus aria-hidden="true" className="size-4" />
            {t('admin.receiving.entryButton')}
          </Button>
        ) : null}
      </header>

      {access.isLoading ? (
        <div
          className="bg-admin-surface-muted h-32 animate-pulse rounded-lg motion-reduce:animate-none"
          data-testid="receiving-access-loading"
        />
      ) : null}

      {access.isError ? (
        <div
          role="alert"
          className="border-border bg-card shadow-admin-card rounded-lg border p-6 text-center"
        >
          <p className="text-muted-foreground mb-4 text-sm">{t('common.errors.networkFailed')}</p>
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            onClick={() => void access.refetch()}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {access.data && !canManage ? <ReceivingNotAvailable /> : null}

      {access.data && canManage ? (
        <div className="space-y-4">
          {!enabled ? <ReceivingPausedBanner draftsReadOnly /> : null}
          <ReceivingList
            kind={kind}
            onKindChange={onKindChange}
            items={items}
            isLoading={list.isLoading}
            isError={list.isError}
            onRetry={() => void list.refetch()}
            hasMore={list.hasNextPage}
            isLoadingMore={list.isFetchingNextPage}
            onLoadMore={() => void list.fetchNextPage()}
          />
        </div>
      ) : null}
    </main>
  );
}
